import _ from 'lodash'
import { reject, flow, mapValues, flatMap, map, uniqBy, isPlainObject, isEmpty, endsWith } from 'lodash/fp'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { ModalHeader, ModalBody } from 'reactstrap'
import { compose, branch, lifecycle, withProps, withHandlers } from 'recompose'
import styled from 'styled-components'

import { submitApplication, validateApplication } from 'e-submission/apps/actions'
import {
  getAllInvalidField,
  getAppWithId,
  getIsInsureForeignLabour,
  getIsPayerForeignLabour,
  isInvalidFNA,
  isInvalidVulnerableCustomer,
} from 'e-submission/apps/selectors'
import EditCard from 'e-submission/components/Card/EditCard'
import Dialog from 'e-submission/components/Dialog'
import { getIcon } from 'e-submission/components/Icon'
import Content from 'e-submission/components/Layout/Content'
import { isSelectedF2F } from 'e-submission/domain/data-model/form/selectors'
import { getToggles } from 'quick-quote/feature-toggles'

const Status = styled.div`
  text-align: center;
  padding: 30px 0px;
  div:last-child {
    padding-top: 30px;
  }
`

const Component = styled(({ className, invalid, invalidFNA, invalidVulnerableCustomer, history, toggle }) => {
  const includeOnEkycPage = ['insured', 'owner']
  const filteredInvalid = history?.location?.pathname.includes('ekyc')
    ? _.pickBy(_.pick(invalid, includeOnEkycPage), (value) => !_.isEmpty(value?.fields))
    : _.pickBy(invalid, (value) => !_.isEmpty(value?.fields))

  return (
    <Dialog className={className} isOpen={!_.isEmpty(filteredInvalid) || invalidFNA || invalidVulnerableCustomer}>
      <ModalHeader toggle={toggle}>
        <div>กรอกข้อมูลไม่ครบ</div>
      </ModalHeader>
      <ModalBody>
        <Status>
          {getIcon('formInvalid')}
          <div>
            ไม่สามารถสร้างเอกสารได้
            <br />
            <b>กรุณากลับไปแก้ไขข้อมูลด้านล่างให้สมบูรณ์</b>
          </div>
        </Status>
        <Content>
          {_.map(filteredInvalid, ({ fields, label }, pageId) => (
            <EditCard key={pageId} title={label} onClick={() => history.push(pageId)}>
              <ul>
                {_.map(fields, (label, index) => (
                  <li key={index}>
                    {getToggles().ENABLE_SHARED_AGENT_SALE_VALIDATION && pageId === 'agent' && label === 'นายหน้า'
                      ? 'กรุณาตรวจสอบสิทธิการขายของตัวแทนอีกท่าน'
                      : label}
                  </li>
                ))}
              </ul>
            </EditCard>
          ))}
          {invalidFNA && (
            <EditCard
              key="FNA"
              title={'แบบสอบถามเพื่อวิเคราะห์ความต้องการซื้อผลิตภัณฑ์ประกันชีวิต'}
              btnText="กลับสู่หน้าแรก"
              onClick={() => history.push('/')}
            >
              <ul>
                <li>ไม่สามารถดำเนินการต่อได้ เนื่องจากไม่มีข้อมูล FNA</li>
                <li>กรุณาเริ่มการขายใหม่</li>
              </ul>
            </EditCard>
          )}
          {invalidVulnerableCustomer && (
            <EditCard
              key="VC"
              title={'แบบสอบถามความเข้าใจ กลุ่มคำถามเปราะบาง'}
              btnText="กลับสู่หน้าแรก"
              onClick={() => history.push('/')}
            >
              <ul>
                <li>ไม่สามารถดำเนินการต่อได้ เนื่องจากไม่มีข้อมูล กลุ่มคำถามเปราะบาง</li>
                <li>กรุณาเริ่มการขายใหม่</li>
              </ul>
            </EditCard>
          )}
        </Content>
      </ModalBody>
    </Dialog>
  )
})`
  .modal-body {
    padding: 0px 0px 32px 0px;
    background: #f5f5f5;
  }
  ul {
    padding-left: 15px;
    margin-bottom: 10px;
    li {
      padding-bottom: 10px;
    }
  }
`

export default compose(
  withRouter,
  connect(
    (state) => ({
      invalid: getAllInvalidField(state),
      invalidFNA: isInvalidFNA(state),
      invalidVulnerableCustomer: isInvalidVulnerableCustomer(state),
      isPayerForeignLabour: getIsPayerForeignLabour(state),
      isInsureForeignLabour: getIsInsureForeignLabour(state),
      isF2F: isSelectedF2F(getAppWithId(state)),
    }),
    (dispatch) => ({
      validateApplication: () => dispatch(validateApplication()),
      submitApplication: () => dispatch(submitApplication()),
    })
  ),
  branch(
    ({ invalid, isF2F, isPayerForeignLabour, isInsureForeignLabour }) => {
      if (isF2F && (isPayerForeignLabour || isInsureForeignLabour)) {
        const filterFieldFromKeys = (data, keys, fieldIdToRemove) => {
          return Object.keys(data).reduce((acc, key) => {
            if (keys.includes(key) && data[key]?.sections) {
              acc[key] = {
                ...data[key],
                sections: data[key].sections.map((section) => ({
                  ...section,
                  fields: section.fields.filter((field) => field.id !== key + fieldIdToRemove),
                })),
              }
            } else {
              acc[key] = data[key]
            }
            return acc
          }, {})
        }

        const exceptionKeys = ['insured', 'payer', 'owner']

        const filteredInvalid = filterFieldFromKeys(invalid, exceptionKeys, '.idNo.passport')

        exceptionKeys.forEach((key) => {
          const ownerFieldId = filteredInvalid['owner']?.sections.find((section) => {
            return section.fields.find((field) => field.id === 'payer.idNo.passport')
          })

          if (key === 'owner' && ownerFieldId) {
            delete filteredInvalid.owner
          } else if (filteredInvalid[key]?.sections.length > 0) {
            const a = filteredInvalid[key].sections.filter(
              (section) => section?.id === 'personal' && section.fields.length === 0
            )
            const b = a.filter((x) => x.id === 'personal' && x.fields.length === 0)
            if (b.length > 0) {
              delete filteredInvalid.insured
            }
          }
        })

        return _.isEmpty(filteredInvalid)
      }

      return _.isEmpty(invalid)
    },
    lifecycle({
      componentDidMount() {
        this.props.submitApplication()
      },
    })
  ),
  lifecycle({
    componentDidMount() {
      this.props.validateApplication()
    },
  }),
  withProps(({ history, invalid, isPayerForeignLabour, isInsureForeignLabour }) => ({
    invalid: mapValues(
      ({ sections, label }) => ({
        label,
        fields: flow(
          map(({ id, fields, label, heading }) => {
            const removePassportFields = (fields) => {
              let filteredFields = fields

              if (isInsureForeignLabour) {
                filteredFields = filteredFields.filter((field) => field.id !== 'insured.idNo.passport')
              }

              if (isPayerForeignLabour) {
                filteredFields = filteredFields
                  .filter((field) => field.id !== 'payer.idNo.passport')
                  .filter((field) => field.id !== 'owner.idNo.passport')
              }

              return filteredFields
            }

            if (history?.location?.pathname?.includes('ekyc')) {
              const allowedLabels = [
                'ชื่อของผู้เอาประกันภัย',
                'นามสกุลของผู้เอาประกันภัย',
                'เลขประจำตัวประชาชน',
                'หมายเลขหนังสือเดินทาง',
                'ชื่อของผู้ชำระเบี้ย',
                'นามสกุลของผู้ชำระเบี้ย',
              ]

              let filteredFields = fields.filter((field) => allowedLabels.includes(field.label))
              filteredFields = removePassportFields(filteredFields)

              return { id, fields: filteredFields }
            }

            if (id === 'personal') {
              const filteredFields = removePassportFields(fields)

              return { id, fields: filteredFields }
            }

            if (endsWith(']', id)) {
              return heading
            }

            if (!isEmpty(label)) {
              return label
            }
            const insuredEkyc = fields.filter((field) => field.id == 'remoteSelling.insured.dopaResult')
            const payerEkyc = fields.filter((field) => field.id == 'remoteSelling.payer.dopaResult')
            if (id === 'ekyc' && insuredEkyc.length > 0) {
              return 'กรุณายืนยันตัวตนของผู้เอาประกันภัย'
            } else if (id === 'ekyc' && payerEkyc.length > 0) {
              return 'กรุณายืนยันตัวตนของผู้เอาชำระเบี้ย'
            }

            if (getToggles().ENABLE_EKYC_F2F) {
              const insuredIsRequired = fields.filter((field) => field.id == 'validationEKYC.insured.isRequired')
              const payerIsRequired = fields.filter((field) => field.id == 'validationEKYC.payer.isRequired')

              if (id === 'ekyc' && insuredIsRequired.length > 0) {
                return 'กรุณายืนยันตัวตนของผู้เอาประกันภัย'
              } else if (id === 'ekyc' && payerIsRequired.length > 0) {
                return 'กรุณายืนยันตัวตนของผู้ชำระเบี้ย'
              }
            }

            return { id, fields }
          }),
          reject(isEmpty),
          uniqBy((value) => (isPlainObject(value) ? value.id : value)),
          flatMap((value) => (isPlainObject(value) ? map('label', value.fields) : value))
        )(sections),
      }),
      invalid
    ),
  })),
  withHandlers({
    toggle: ({ history, invalid }) => () => (_.keys(invalid)[0] ? history.push(_.keys(invalid)[0]) : history.goBack()),
  })
)(Component)
